import React, { useState } from 'react';
import useTranslation from '../../../hooks/useTranslation';
import { DisplayAddress } from '../../../utils/addressDisplay';

interface AddressLinesProps {
  display: DisplayAddress;
  className?: string;
  /** 도로명·지번이 모두 있을 때 "두 주소 체계" 안내(ⓘ 인라인 확장)를 노출한다. 주소설정 화면 전용. */
  showFormatInfo?: boolean;
}

const PinIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
    data-testid="address-pin"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" strokeWidth={2} />
    <path strokeLinecap="round" strokeWidth={2} d="M12 11.5v4.5" />
    <path strokeLinecap="round" strokeWidth={2} d="M12 8h.01" />
  </svg>
);

const AddressLines: React.FC<AddressLinesProps> = ({ display, className, showFormatInfo }) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  const { road, jibun, detail, fallbackLine } = display;
  const hasAxes = !!(road || jibun);
  const isDual = !!(road && jibun);
  const canShowInfo = isDual && !!showFormatInfo;

  // 표시할 내용이 아무것도 없으면 빈 상자를 그리지 않는다.
  if (!hasAxes && !fallbackLine && !detail) {
    return null;
  }

  return (
    <div className={`flex items-start gap-2 text-left ${className ?? ''}`}>
      {/* 핀은 라벨(캡션)이 없는 단일 줄에서만 프레이밍 역할을 한다. dual/캡션이 있으면 생략. */}
      {!hasAxes && <PinIcon />}
      <div className="min-w-0 flex-1">
        {hasAxes ? (
          <>
            <dl className="m-0">
              {road && (
                <div>
                  <div className="flex items-center gap-1.5">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-red-600">
                      Road
                    </dt>
                    {canShowInfo && (
                      <button
                        type="button"
                        onClick={() => setInfoOpen((open) => !open)}
                        aria-expanded={infoOpen}
                        aria-label={t('address.format.infoLabel')}
                        className="flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        <InfoIcon />
                      </button>
                    )}
                  </div>
                  <dd className="m-0 mt-0.5 text-gray-800 text-[15px] leading-snug break-words">
                    {road}
                  </dd>
                </div>
              )}
              {jibun && (
                <div className={road ? 'mt-2.5' : undefined}>
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Lot no.
                  </dt>
                  <dd className="m-0 mt-0.5 text-gray-500 text-sm leading-snug break-words">
                    {jibun}
                  </dd>
                </div>
              )}
            </dl>
            {canShowInfo && infoOpen && (
              <p className="mt-2.5 text-[12.5px] text-gray-500 leading-relaxed bg-gray-50 rounded-md px-3 py-2">
                {t('address.format.info')}
              </p>
            )}
          </>
        ) : (
          fallbackLine && (
            <p className="m-0 text-gray-800 text-[15px] leading-snug break-words">{fallbackLine}</p>
          )
        )}
        {detail && (
          <p
            className={`mb-0 text-gray-400 text-sm leading-snug break-words ${
              hasAxes || fallbackLine ? 'mt-2.5' : ''
            }`}
          >
            {detail}
          </p>
        )}
      </div>
    </div>
  );
};

export default AddressLines;
